import { animatePulse } from "@/src/utils/animation.utils";
import { useEffect, useRef } from "react";
import { Animated, View } from "react-native";

export function useSkeletonOpacity() {
  const opacity = useRef(new Animated.Value(0.3)).current;
  useEffect(() => {
    animatePulse(opacity);
  }, []);
  return opacity;
}

const Block = ({ className, style }: { className: string; style?: object }) => (
  <View className={`rounded-md bg-ash-3 ${className}`} style={style} />
);

export const GlobalNewsSkeleton = () => {
  const opacity = useSkeletonOpacity();

  return (
    <Animated.View
      style={{ opacity }}
      className="flex border border-ash-3 rounded-2xl bg-chalk p-4 flex-col gap-4"
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <GlobalNewsItemSkeleton key={i} />
      ))}
    </Animated.View>
  );
};

export function GlobalNewsItemSkeleton() {
  return (
    <View className="self-stretch border border-ash-3 rounded-2xl p-2 pl-3 bg-chalk gap-2 mb-2">
      <View className="gap-1 mb-1">
        <Block className="h-3 w-full" />
        <Block className="h-3 w-4/5" />
      </View>
      <Block className="h-3 w-20" />
      <View className="flex-row gap-2 my-1">
        <Block className="h-5 w-16 rounded-2xl" />
        <Block className="h-5 w-20 rounded-2xl" />
      </View>
      <View className="gap-1">
        <Block className="h-3 w-full" />
        <Block className="h-3 w-11/12" />
        <Block className="h-3 w-3/4" />
      </View>
    </View>
  );
}

export const NewsGridSkeleton = ({ columns = 2 }: { columns?: 1 | 2 | 3 }) => {
  const opacity = useSkeletonOpacity();
  const cards = columns === 3 ? 6 : columns === 2 ? 4 : 3;

  return (
    <Animated.View style={{ opacity }} className="flex-1 pt-4">
      <View className="flex-row flex-wrap gap-3">
        {Array.from({ length: cards }).map((_, i) => (
          <NewsGridCardSkeleton key={i} columns={columns} />
        ))}
      </View>
    </Animated.View>
  );
};

function NewsGridCardSkeleton({ columns }: { columns: 1 | 2 | 3 }) {
  const widthStyle =
    columns === 3
      ? { width: "32.4%" as const }
      : columns === 2
        ? { width: "49%" as const }
        : { width: "100%" as const };

  return (
    <View
      className="bg-chalk rounded-xl md:rounded-2xl md:border md:border-ash-3 md:mb-4 overflow-hidden p-2 gap-2"
      style={widthStyle}
    >
      <Block className="w-full rounded-lg" style={{ height: 128 }} />

      <View className="flex-row items-center gap-2 px-1">
        <Block className="h-8 w-8 rounded-full" />
        <Block className="h-3 flex-1" />
        <Block className="h-5 w-5 rounded-md" />
      </View>

      <View className="px-1 gap-1">
        <Block className="h-3 w-full" />
        <Block className="h-3 w-4/5" />
      </View>

      <View className="flex-row gap-2 px-1">
        <Block className="h-3 w-10" />
        <Block className="h-3 w-16" />
      </View>
    </View>
  );
}
